import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { calculatePnl } from "@/utils/pnl";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateForInput, getCurrentDateIST } from "@/lib/dateUtils";

export const EditTrade = () => {
  const { tradeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnUrl || "/trades";
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [symbols, setSymbols] = useState({ NSE: [], MCX: [] });
  const [accounts, setAccounts] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [formData, setFormData] = useState({
    symbol: "",
    exchange: "NSE",
    direction: "LONG",
    entry_date: "",
    entry_price: "",
    num_lots: "1",
    lot_size: "",
    exit_date: "",
    exit_price: "",
    entry_comments: "",
    exit_comments: "",
    status: "OPEN",
    account_id: "",
    strategy_id: "",
    strategy_name: "",
  });

  useEffect(() => {
    fetchSymbols();
    fetchAccounts();
    fetchStrategies();
    fetchTrade();
  }, [tradeId]);

  const fetchSymbols = async () => {
    try {
      const response = await api.get("/symbols");
      setSymbols(response.data);
    } catch (error) {
      console.error("Error fetching symbols:", error);
      toast.error("Failed to load symbols");
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const response = await api.get("/strategies");
      setStrategies(response.data);
    } catch (error) {
      console.error("Error fetching strategies:", error);
    }
  };

  const fetchTrade = async () => {
    try {
      const response = await api.get(`/trades/${tradeId}`);
      const trade = response.data;
      
      // Format dates for HTML date input (YYYY-MM-DD) using IST timezone
      setFormData({
        symbol: trade.symbol,
        exchange: trade.exchange,
        direction: trade.direction,
        entry_date: formatDateForInput(trade.entry_date),
        entry_price: trade.entry_price.toString(),
        num_lots: trade.num_lots.toString(),
        lot_size: trade.lot_size.toString(),
        exit_date: formatDateForInput(trade.exit_date),
        exit_price: trade.exit_price ? trade.exit_price.toString() : "",
        entry_comments: trade.entry_comments || "",
        exit_comments: trade.exit_comments || "",
        status: trade.status,
        account_id: trade.account_id || "",
        strategy_id: trade.strategy_id || "",
        strategy_name: trade.strategy_name || "",
      });
    } catch (error) {
      console.error("Error fetching trade:", error);
      toast.error(error.userMessage || "Failed to load trade");
      navigate(returnUrl);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async (e, closeAfterUpdate = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        symbol: formData.symbol,
        exchange: formData.exchange,
        direction: formData.direction,
        entry_date: formData.entry_date,
        entry_price: parseFloat(formData.entry_price),
        num_lots: parseInt(formData.num_lots),
        lot_size: parseInt(formData.lot_size),
        entry_comments: formData.entry_comments,
        account_id: (formData.account_id && formData.account_id !== "none") ? formData.account_id : null,
        strategy_id: (formData.strategy_id && formData.strategy_id !== "none") ? formData.strategy_id : null,
      };

      // If closing the trade, require exit price and calculate P&L
      if (closeAfterUpdate) {
        if (!formData.exit_price) {
          toast.error("Exit price is required to close the trade");
          setLoading(false);
          return;
        }
        
        const exitPrice = parseFloat(formData.exit_price);
        const entryPrice = parseFloat(formData.entry_price);
        const direction = formData.direction;
        // Calculate P&L using correct multiplier
        const { pnlPoints, pnlAmount } = calculatePnl({
          direction, entryPrice: entryPrice, exitPrice: exitPrice,
          numLots: parseInt(formData.num_lots),
          trade: formData,
        });
        
        payload.exit_date = formData.exit_date || getCurrentDateIST();
        payload.exit_price = exitPrice;
        payload.exit_comments = formData.exit_comments;
        payload.pnl_points = pnlPoints;
        payload.pnl_amount = pnlAmount;
        payload.status = "CLOSED";
      } else {
        // Only include exit fields if provided (for update without closing)
        if (formData.exit_date) {
          payload.exit_date = formData.exit_date;
        }
        if (formData.exit_price) {
          payload.exit_price = parseFloat(formData.exit_price);
        }
        if (formData.exit_comments) {
          payload.exit_comments = formData.exit_comments;
        }
      }

      console.log("Updating trade with payload:", JSON.stringify(payload, null, 2));
      
      const response = await api.put(`/trades/${tradeId}`, payload);
      console.log("Update response:", response.data);
      
      toast.success(closeAfterUpdate ? "Trade updated and closed successfully" : "Trade updated successfully");
      navigate(returnUrl);
    } catch (error) {
      console.error("Error updating trade:", error);
      toast.error(error.userMessage || "Failed to update trade");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (fetchLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400 font-mono">Loading trade...</div>
      </div>
    );
  }

  const currentSymbols = symbols[formData.exchange] || [];

  // Direction-based styling
  const directionColor = formData.direction === "LONG" ? "border-profit/50" : "border-loss/50";
  const directionBg = formData.direction === "LONG" ? "bg-profit/5" : "bg-loss/5";
  const directionBadge = formData.direction === "LONG" 
    ? "bg-profit/20 text-profit border-profit/30" 
    : "bg-loss/20 text-loss border-loss/30";

  return (
    <div data-testid="edit-trade-page">
      <button
        onClick={() => navigate(returnUrl)}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Trades
      </button>

      <div className="mb-8 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[260px]">
          <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl text-white mb-2">
            Edit Trade - {formData.symbol || "Loading..."}
          </h1>
          <p className="text-slate-400 text-base">Update trade details and exit information</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span
              data-testid="header-exchange-badge"
              className="px-2.5 py-1 rounded-sm border border-white/10 bg-black/30 text-xs font-mono text-slate-300"
              title="Exchange"
            >
              {formData.exchange || "—"}
            </span>
            {formData.strategy_name && (
              <span
                data-testid="header-strategy-name-badge"
                className="px-2.5 py-1 rounded-sm border border-violet-400/30 bg-violet-500/10 text-xs font-mono text-violet-300"
                title="Strategy label (free-text)"
              >
                {formData.strategy_name}
              </span>
            )}
          </div>
        </div>
        {/* Direction Badge */}
        <div className={`px-4 py-2 rounded-md border ${directionBadge} font-semibold text-lg`}>
          {formData.direction}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl">
        <div className={`bg-charcoal/50 border-2 ${directionColor} ${directionBg} rounded-md p-6 space-y-6 transition-colors`}>
          {/* Account and Strategy Selection */}
          <div>
            <h3 className="text-lg font-heading font-semibold text-white mb-4">Account & Strategy</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account_id" className="text-slate-300 text-sm font-medium">
                  Account
                </Label>
                <Select
                  value={formData.account_id}
                  onValueChange={(value) => handleChange("account_id", value)}
                >
                  <SelectTrigger
                    id="account_id"
                    data-testid="account-select"
                    className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                  >
                    <SelectValue placeholder="Select Account" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="none">No Account</SelectItem>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} {account.broker && `(${account.broker})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="strategy_id" className="text-slate-300 text-sm font-medium">
                  Strategy
                </Label>
                <Select
                  value={formData.strategy_id}
                  onValueChange={(value) => handleChange("strategy_id", value)}
                >
                  <SelectTrigger
                    id="strategy_id"
                    data-testid="strategy-select"
                    className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                  >
                    {formData.strategy_name && !formData.strategy_id ? (
                      <span data-testid="strategy-name-display" className="text-violet-300 font-mono text-sm">
                        {formData.strategy_name}
                      </span>
                    ) : (
                      <SelectValue placeholder="Select Strategy" />
                    )}
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="none">No Strategy</SelectItem>
                    {strategies.map((strategy) => (
                      <SelectItem key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Entry Section */}
          <div>
            <h3 className="text-lg font-heading font-semibold text-white mb-4">Entry Details</h3>
            
            {/* Exchange and Symbol */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label htmlFor="exchange" className="text-slate-300 text-sm font-medium">
                  Exchange
                </Label>
                <Select
                  value={formData.exchange}
                  onValueChange={(value) => handleChange("exchange", value)}
                >
                  <SelectTrigger
                    id="exchange"
                    data-testid="exchange-select"
                    className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="NSE">NSE</SelectItem>
                    <SelectItem value="NFO">NFO</SelectItem>
                    <SelectItem value="BSE">BSE</SelectItem>
                    <SelectItem value="BFO">BFO</SelectItem>
                    <SelectItem value="MCX">MCX</SelectItem>
                    <SelectItem value="CDS">CDS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="symbol" className="text-slate-300 text-sm font-medium">
                  Symbol
                </Label>
                <Input
                  id="symbol"
                  data-testid="symbol-input"
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => handleChange("symbol", e.target.value)}
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                  required
                />
              </div>
            </div>

            {/* Direction and Entry Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label htmlFor="direction" className="text-slate-300 text-sm font-medium">
                  Direction
                </Label>
                <Select
                  value={formData.direction}
                  onValueChange={(value) => handleChange("direction", value)}
                >
                  <SelectTrigger
                    id="direction"
                    data-testid="direction-select"
                    className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="LONG">LONG</SelectItem>
                    <SelectItem value="SHORT">SHORT</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="entry_date" className="text-slate-300 text-sm font-medium">
                  Entry Date
                </Label>
                <Input
                  id="entry_date"
                  data-testid="entry-date-input"
                  type="date"
                  value={formData.entry_date}
                  onChange={(e) => handleChange("entry_date", e.target.value)}
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                  required
                />
              </div>
            </div>

            {/* Entry Price and Lots */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="space-y-2">
                <Label htmlFor="entry_price" className="text-slate-300 text-sm font-medium">
                  Entry Price (₹)
                </Label>
                <Input
                  id="entry_price"
                  data-testid="entry-price-input"
                  type="number"
                  step="0.01"
                  value={formData.entry_price}
                  onChange={(e) => handleChange("entry_price", e.target.value)}
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="num_lots" className="text-slate-300 text-sm font-medium">
                  Number of Lots
                </Label>
                <Input
                  id="num_lots"
                  data-testid="num-lots-input"
                  type="number"
                  value={formData.num_lots}
                  onChange={(e) => handleChange("num_lots", e.target.value)}
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                  min="1"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lot_size" className="text-slate-300 text-sm font-medium">
                  Lot Size
                </Label>
                <Input
                  id="lot_size"
                  data-testid="lot-size-input"
                  type="number"
                  value={formData.lot_size}
                  onChange={(e) => handleChange("lot_size", e.target.value)}
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                  min="1"
                  required
                />
              </div>
            </div>

            {/* Entry Comments */}
            <div className="space-y-2">
              <Label htmlFor="entry_comments" className="text-slate-300 text-sm font-medium">
                Entry Comments
              </Label>
              <Textarea
                id="entry_comments"
                data-testid="entry-comments-input"
                value={formData.entry_comments}
                onChange={(e) => handleChange("entry_comments", e.target.value)}
                className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-sm text-white min-h-[80px]"
                placeholder="Entry strategy notes..."
              />
            </div>
          </div>

          {/* Exit Section */}
          <div className="border-t border-white/10 pt-6">
            <h3 className="text-lg font-heading font-semibold text-white mb-4">Exit Details</h3>
            
            {/* Exit Date and Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label htmlFor="exit_date" className="text-slate-300 text-sm font-medium">
                  Exit Date (Optional)
                </Label>
                <Input
                  id="exit_date"
                  data-testid="exit-date-input"
                  type="date"
                  value={formData.exit_date}
                  onChange={(e) => handleChange("exit_date", e.target.value)}
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="exit_price" className="text-slate-300 text-sm font-medium">
                  Exit Price (₹) (Optional)
                </Label>
                <Input
                  id="exit_price"
                  data-testid="exit-price-input"
                  type="number"
                  step="0.01"
                  value={formData.exit_price}
                  onChange={(e) => handleChange("exit_price", e.target.value)}
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Exit Comments */}
            <div className="space-y-2">
              <Label htmlFor="exit_comments" className="text-slate-300 text-sm font-medium">
                Exit Comments (Optional)
              </Label>
              <Textarea
                id="exit_comments"
                data-testid="exit-comments-input"
                value={formData.exit_comments}
                onChange={(e) => handleChange("exit_comments", e.target.value)}
                className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-sm text-white min-h-[80px]"
                placeholder="Exit strategy notes..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
            <button
              type="submit"
              data-testid="update-trade-button"
              disabled={loading}
              className="bg-white text-black hover:bg-slate-200 rounded-sm font-medium px-6 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Trade"}
            </button>
            {formData.status === "OPEN" && (
              <button
                type="button"
                data-testid="update-close-trade-button"
                disabled={loading}
                onClick={(e) => handleSubmit(e, true)}
                className="bg-loss/20 text-loss hover:bg-loss/30 border border-loss/50 rounded-sm font-medium px-6 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Closing..." : "Update & Close Trade"}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(returnUrl)}
              className="bg-white/10 text-white hover:bg-white/20 rounded-sm font-medium px-6 py-3 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditTrade;